import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Wallet as WalletIcon, User, Briefcase, Zap, CreditCard, DollarSign, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const Wallet = () => {
  const { t } = useLanguage();

  const walletFeatures = [
    {
      icon: Zap,
      title: t('wallet.features.instant'),
      desc: 'Withdraw your money to your bank account instantly',
    },
    {
      icon: CreditCard,
      title: t('wallet.features.iban'),
      desc: 'Your wallet comes with a dedicated IBAN',
    },
    {
      icon: DollarSign,
      title: t('wallet.features.topup'),
      desc: 'Multiple ways to add money to your wallet',
    },
    {
      icon: TrendingUp,
      title: t('wallet.features.payout'),
      desc: 'Fast payouts for merchants and businesses',
    },
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="heading-1 mb-6">{t('wallet.title')}</h1>
            <p className="heading-3 text-gray-600 mb-4">{t('wallet.subtitle')}</p>
            <p className="text-lg text-gray-600">{t('wallet.description')}</p>
          </div>

          {/* Wallet Display */}
          <div className="mt-12 flex flex-col md:flex-row gap-8 justify-center items-center">
            {/* Personal Wallet */}
            <div
              className="relative group"
              style={{
                width: '320px',
                height: '180px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                borderRadius: '20px',
                padding: '24px',
                boxShadow: '0 20px 40px rgba(16, 185, 129, 0.3)',
                transition: 'transform 0.3s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <div className="flex items-center gap-2 mb-6">
                <User className="h-5 w-5 text-white" />
                <span className="text-white font-semibold">{t('wallet.personal')}</span>
              </div>
              <div className="text-white text-3xl font-bold mb-2">SAR 0.00</div>
              <div className="text-white/80 text-sm">{t('wallet.personalDesc')}</div>

              {/* SAR Logo */}
              <img
                src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/1tnawu9j_riyal%20logo.jpeg"
                alt="SAR"
                className="absolute bottom-4 right-4 w-8 h-8 opacity-30"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>

            {/* Merchant Wallet */}
            <div
              className="relative group"
              style={{
                width: '320px',
                height: '180px',
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                borderRadius: '20px',
                padding: '24px',
                boxShadow: '0 20px 40px rgba(245, 158, 11, 0.3)',
                transition: 'transform 0.3s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <div className="flex items-center gap-2 mb-6">
                <Briefcase className="h-5 w-5 text-white" />
                <span className="text-white font-semibold">{t('wallet.merchant')}</span>
              </div>
              <div className="text-white text-3xl font-bold mb-2">SAR 0.00</div>
              <div className="text-white/80 text-sm">{t('wallet.merchantDesc')}</div>

              {/* SAR Logo */}
              <img
                src="https://customer-assets.emergentagent.com/job_a49fc96e-c6d1-4538-8241-3e5dffe114fb/artifacts/1tnawu9j_riyal%20logo.jpeg"
                alt="SAR"
                className="absolute bottom-4 right-4 w-8 h-8 opacity-30"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="heading-2 text-center mb-12">Wallet Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {walletFeatures.map((feature, index) => (
              <Card key={index} className="product-card">
                <CardContent className="p-6 text-center">
                  <div
                    className="inline-flex p-4 rounded-full mb-4"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
                    }}
                  >
                    <feature.icon className="h-8 w-8" style={{ color: '#10B981' }} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <h2 className="heading-2 text-center mb-12">Choose Your Wallet</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Personal Wallet Details */}
            <Card className="product-card">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="p-3 rounded-xl"
                    style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                  >
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">{t('wallet.personal')}</h3>
                </div>

                <ul className="space-y-3">
                  {[
                    'Send and receive money instantly',
                    'Pay with QR codes',
                    'Top-up from any bank',
                    'Instant withdrawals',
                    'Transaction history',
                    'No monthly fees',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Merchant Wallet Details */}
            <Card className="product-card">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="p-3 rounded-xl"
                    style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
                  >
                    <Briefcase className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">{t('wallet.merchant')}</h3>
                </div>

                <ul className="space-y-3">
                  {[
                    'Accept payments from customers',
                    'POS integration',
                    'Payment gateway access',
                    'Dedicated IBAN',
                    'Bulk payouts',
                    'Advanced analytics',
                    'Multi-user access',
                    'Priority support',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button className="btn-primary text-lg px-8 py-6">Open Your Wallet Today</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Wallet;
